/**
 * FASE 22.8 — WORKFLOW MEMORY
 *
 * Compact operational summary for prompt injection.
 * Output is a short text string (max ~300 chars) describing the
 * current workflow state to help Kevia give contextual responses.
 */

import { getActiveWorkflows } from "./workflowState";
import { getWorkflowDefinition, PRIORITY } from "./workflowSchemas";
import { getPriorityWorkflows } from "./workflowPriorities";
import { computeFollowupItems } from "./followupEngine";

const MAX_MEMORY_CHARS = 300;

/**
 * Build a compact workflow context string for LLM injection.
 * Returns null when no active workflows exist.
 * @returns {string|null}
 */
export function buildWorkflowMemory() {
  const active = getActiveWorkflows();
  if (active.length === 0) return null;

  const parts = [];

  // Summary line
  const overdueItems = computeFollowupItems().filter((i) => i.kind === "overdue");
  parts.push(`[workflows: ${active.length} activo${active.length !== 1 ? "s" : ""}${overdueItems.length > 0 ? `, ${overdueItems.length} vencido${overdueItems.length !== 1 ? "s" : ""}` : ""}]`);

  // Top 3 by priority
  const top = getPriorityWorkflows(3);
  for (const wf of top) {
    const def        = getWorkflowDefinition(wf.type);
    const entityName = wf.related_entities?.[0]?.name;
    const pct        = Math.round((wf.progress ?? 0) * 100);
    const nameLabel  = entityName ? ` (${entityName})` : "";
    parts.push(`• ${def?.label ?? wf.type}${nameLabel} — ${pct}% · ${wf.priority}`);
  }

  const text = parts.join("\n");
  return text.length > MAX_MEMORY_CHARS ? text.slice(0, MAX_MEMORY_CHARS) + "…" : text;
}

/**
 * Returns a serializable summary object for persistence (snapshot).
 * Contains only metadata — no pastoral content.
 * @returns {{ count: number, critical: number, high: number, types: string[] }}
 */
export function compactWorkflowSummary() {
  const active = getActiveWorkflows();
  return {
    count:    active.length,
    critical: active.filter((w) => w.priority === PRIORITY.CRITICAL).length,
    high:     active.filter((w) => w.priority === PRIORITY.HIGH).length,
    types:    [...new Set(active.map((w) => w.type))],
  };
}
